import { Capacitor } from '@capacitor/core';
import { matchPath, Navigate, useLocation } from 'react-router-dom';

/**
 * Native route fence.
 *
 * The Capacitor app ships the entire web bundle, so every web route is
 * reachable inside the native webview. Web marketing surfaces (funnels,
 * SEO articles, creator marketing) carry web offer copy and Stripe
 * checkouts that must never render in the app; before this fence,
 * correctness depended on each page carrying its own `if native` branch,
 * which is how the Aug 2026 stale-trial-copy incident happened (PR #523).
 *
 * Patterns here are react-router patterns matched against the current
 * pathname. On a native runtime, a match redirects to "/", where
 * NativeAwareRootRoute routes the user into the app.
 *
 * Deliberately NOT fenced:
 * - "/p/:id", "/e/:id", "/j/:id": public landing pages native guests use
 *   (except the Challenge's "/j/" sales page, fenced below).
 * - "/challenge/:challengeId/learn": purchased a la carte content.
 * - "/terms", "/privacy", "/contact", "/support", "/advisory-board",
 *   "/podcast": informational pages linked from inside the app.
 */
export const WEB_ONLY_PATH_PATTERNS: readonly string[] = [
  // Web pricing and Stripe checkout. Native upgrades use PremiumPaywall and
  // the native app-store billing bridge instead.
  '/pricing',

  // Live funnel (email-first web checkout)
  '/journal-party-live',
  '/journal-party-live/*',

  // Challenge funnel (web Stripe checkout). The Challenge is sold only through
  // its web funnel, so its sales page never renders in the app: that covers
  // the "/j/" alias of the funnel and every legacy "/challenge/:slug"
  // redirect into it. "/challenge/:challengeId/learn" (two segments) stays
  // reachable for buyers.
  '/challenge',
  '/challenge/:slug',
  '/j/21-day-fill-your-journal-challenge-4797ab-journey',
  '/challenge/premium-offer',
  '/challenge/premium-downsell',
  '/challenge/confirmation',

  // Journey funnels ($19 Journeys sold on the web only; never listed,
  // priced or linked in the app). Exact "/journal" stays reachable: it is the
  // legacy redirect to /home.
  '/journal/:seoSlug',
  '/journal/:seoSlug/*',

  // Physical goods funnel
  '/bundle',
  '/bundle/thanks',

  // Creator and company marketing
  '/for-creators',
  '/for-creators/*',
  '/about',

  // SEO content library
  '/journal-prompts',
  '/journal-prompts/*',
  '/daily-journal-prompts',
  '/what-to-write-in-a-journal',
  '/journal-prompt-of-the-day',
  '/journal-prompt-generator',
  '/guided-journaling',
  '/guided-journaling-app',
  '/private-journaling-app',
  '/ai-journaling-alternative',
  '/therapist-reviewed-journal-prompts',
  '/best-guided-journaling-platform',
  '/best-journaling-apps',
  '/best-journaling-app-with-prompts',
  '/journal-prompts-for-anxiety',
  '/journal-prompts-for-mental-health',
  '/journal-prompts-for-self-growth',
  '/journal-prompts-app-for-mental-health',
  '/daily-journaling-prompts-for-mental-health',
  '/journaling-for-anxiety',
  '/journaling-for-depression',
  '/journaling-techniques',
  '/journaling-ideas',
  '/journaling-club',
  '/journaling-challenge',
  '/gratitude-journaling',
  '/things-to-be-grateful-for',
  '/gratitude-journal-app',
  '/group-journaling',
  '/journal-sessions',
  '/365-daily-journal-prompts',
  '/faq/*',
  '/post/*',
];

export function isWebOnlyPath(pathname: string): boolean {
  return WEB_ONLY_PATH_PATTERNS.some(
    (pattern) => matchPath(pattern, pathname) !== null,
  );
}

/**
 * Wraps <Routes> so that on a native runtime a web-only path redirects to
 * "/" INSTEAD of rendering the route tree. Wrapping (rather than a sibling
 * <Navigate>) matters: a sibling redirect would let the fenced page mount
 * for one render and fire its effects (Stripe config queries, tracking,
 * checkout-return reconciliation) before navigation takes effect.
 */
export function NativeRouteFence({ children }: { children: React.ReactNode }) {
  const location = useLocation();
  if (Capacitor.isNativePlatform() && isWebOnlyPath(location.pathname)) {
    return <Navigate to="/" replace />;
  }
  return <>{children}</>;
}
